'use client';
import { Transition, Variants } from 'framer-motion';
import { HTMLMotionProps } from 'motion/react';
import * as motion from 'motion/react-client';
import React from 'react';

import { HTMLElements } from '@/lib/motion';

const transitionKeys = [
  'staggerChildren',
  'delayChildren',
  'delay',
  'duration',
  'staggerDirection',
  'repeatDelay',
];

function stripTransition<T extends Partial<Transition>>(transition: T): T {
  const newTransition = { ...transition };
  for (const _key of transitionKeys) {
    const key = _key as keyof Transition;

    if (key in newTransition) {
      newTransition[key] = 0 as Transition[keyof Transition];
    }
  }
  return newTransition;
}

function sanitizeAnimationObject(obj: any): any {
  if (!obj || typeof obj !== 'object') return obj;

  const clone = { ...obj };

  if ('transition' in clone && typeof clone.transition === 'object') {
    clone.transition = stripTransition(clone.transition);
  }

  return clone;
}

export function stripTransitionTimingsFromProps<T extends Record<string, any>>(
  props: T
): T {
  const sanitizedProps = { ...props };
  const animationKeys = [
    'initial',
    'animate',
    'exit',
    'whileHover',
    'whileTap',
    'whileFocus',
    'whileInView',
    'variants',
  ];

  for (const _key of animationKeys) {
    const key = _key as keyof T;
    if (key in sanitizedProps) {
      const value = sanitizedProps[key] as T[keyof T];
      if (typeof value === 'string') continue;

      if (key === 'variants' && typeof value === 'object') {
        const newVariants: Variants = {};
        for (const variantKey in value) {
          newVariants[variantKey] = sanitizeAnimationObject(value[variantKey]);
        }

        sanitizedProps[key] = newVariants as T[keyof T];
      } else {
        sanitizedProps[key] = sanitizeAnimationObject(value);
      }
    }
  }

  return sanitizedProps;
}

export type AnimatedElementProps<T extends keyof HTMLElements> = {
  el: T;
  shouldStripAnimations?: boolean;
} & HTMLMotionProps<T>;

function AnimatedElement<T extends keyof HTMLElements>({
  el,
  shouldStripAnimations = false,
  ...props
}: AnimatedElementProps<T> & {
  children?: React.ReactNode;
}) {
  const MotionElement = motion[el as keyof typeof motion];

  let finalProps = {
    ...props,
  };

  if (shouldStripAnimations) {
    finalProps = stripTransitionTimingsFromProps({ ...props });
  }

  if (!MotionElement) {
    throw new Error(
      `Motion element for "${el}" not found. Ensure the element is supported by motion/react-client.`
    );
  }
  // SuperComplicated type that is effecitvely a union of all HTML elements
  // Type instantiation is excessively deep and possibly infinite.
  // eslint-disable-next-line @typescript-eslint/ban-ts-comment
  // @ts-ignore
  return <MotionElement {...finalProps} />;
}

export default AnimatedElement;
